import { idSchema } from '@/lib/utils'
import { z } from 'zod/v4'

const temporaryHoursSchema = z
  .object({
    is_open: z.boolean(),
    open: z.string().nullable().optional(),
    close: z.string().nullable().optional(),
  })
  .nullable()
  .optional()

const availabilityChangeFieldsSchema = z.object({
  id: idSchema('availability change').optional(),
  availabilityChangeId: idSchema('availability change').optional(),
  organization_id: idSchema('organization'),
  location_ids: z.array(idSchema('location')).min(1, 'Choose at least one location.'),
  start_date: z.iso.date('You must select a start date.'),
  end_date: z.iso.date('You must select an end date.'),
  store_closed: z.boolean(),
  pickup_paused: z.boolean(),
  delivery_paused: z.boolean(),
  designers_choice_only: z.boolean(),
  temporary_hours: z
    .object({
      pickup: temporaryHoursSchema,
      delivery: temporaryHoursSchema,
    })
    .nullable()
    .optional(),
  repeats_annually: z.boolean(),
  holiday_key: z.string().nullable().optional(),
  start_offset_days: z.number().nullable().optional(),
  end_offset_days: z.number().nullable().optional(),
})

/** The effect fields the availability checks read. */
type AvailabilityChangeEffects = Pick<
  z.infer<typeof availabilityChangeFieldsSchema>,
  'store_closed' | 'pickup_paused' | 'delivery_paused' | 'designers_choice_only' | 'temporary_hours'
>

export function availabilityChangeHasEffect(value: AvailabilityChangeEffects) {
  return Boolean(
    value.store_closed ||
    value.pickup_paused ||
    value.delivery_paused ||
    value.designers_choice_only ||
    value.temporary_hours?.pickup?.is_open ||
    value.temporary_hours?.delivery?.is_open
  )
}

export function availabilityChangeCanLimitDesignersChoice(value: AvailabilityChangeEffects) {
  return !(value.pickup_paused && value.delivery_paused)
}

export function availabilityChangeCanMarkStoreClosed(value: AvailabilityChangeEffects) {
  return !value.store_closed || value.pickup_paused || value.delivery_paused
}

export const availabilityChangeSchema = availabilityChangeFieldsSchema.superRefine((value, ctx) => {
  if (availabilityChangeHasEffect(value)) {
    if (!availabilityChangeCanMarkStoreClosed(value)) {
      ctx.addIssue({
        code: 'custom',
        path: ['store_closed'],
        message: 'Store closed must pause pickup or delivery.',
      })
    }

    if (value.designers_choice_only && !availabilityChangeCanLimitDesignersChoice(value)) {
      ctx.addIssue({
        code: 'custom',
        path: ['designers_choice_only'],
        message: "Designer's Choice only applies when pickup or delivery is still available.",
      })
    }

    return
  }

  ctx.addIssue({
    code: 'custom',
    path: ['store_closed'],
    message: 'Choose at least one availability change or set temporary hours.',
  })
})

const availabilityChangeLayoutSchema = z.enum(['calendar', 'list'])

export const availabilityChangeSearchSchema = z.object({
  layout: availabilityChangeLayoutSchema.catch('calendar'),
  month: z.string().optional().catch(undefined),
  date: z.iso.date().optional().catch(undefined),
})
